// eslint-disable-next-line max-len
/* eslint-disable @typescript-eslint/unbound-method, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  ViewChild,
  signal
} from '@angular/core';
import { ToastController } from '@gruposaltaedu/liv-design-system/components';
import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { EMPTY, Observable, Subject, interval } from 'rxjs';
import { catchError, finalize, take, takeUntil } from 'rxjs/operators';
import { ApiLiv } from 'src/app/core/services/api/api-liv.service';
import { BasicUtils } from 'src/app/shared/utils/basic.utils';
import { ModalService } from 'src/app/ui/components/modal/modal.service';

import {
  OfflineShaka,
  ProgressDownload,
  ShakaError,
  ShakaErrorCode,
  ShakaErrorSeverity
} from '../../classes/offline-shaka';
import { SerieFormat } from '../../types/serie';
import {
  EOfflineStorageVideoType,
  ShakaPlayerInstance
} from '../../types/shaka-player.types';
import { VideoFormat } from '../../types/video';
import {
  CopyLinkModalComponent,
  CopyVideoLinkModalData
} from '../copy-link-modal/copy-link-modal.component';

declare let shaka: any;

type Video = VideoFormat & {
  offlineUri?: string;
  share?: {
    link?: string;
    loading: boolean;
  };
  isDownloading?: boolean;
  isInQueue?: boolean; // Novo estado para indicar se está na fila
  download$?: Observable<{
    progress: number;
    completed: boolean;
  }>;
};

export const LOCATION_TOKEN = new InjectionToken<Location>(
  'window location object'
);

const MAX_CONCURRENT_DOWNLOADS_LIMIT = 3;

@Component({
  selector: 'liv-serie-video-shaka',
  templateUrl: './serie-video-shaka.component.html',
  styleUrls: ['./serie-video-shaka.component.scss'],
  providers: [
    {
      provide: LOCATION_TOKEN,
      useValue: window.location
    }
  ],
  standalone: false
})
export class SerieVideoShakaComponent implements OnDestroy, AfterViewInit {
  @ViewChild('videoPlayer', { static: false })
  protected videoElement: ElementRef<HTMLVideoElement>;
  @ViewChild('videoContainer', { static: false })
  protected videoContainerElement: ElementRef<HTMLVideoElement>;
  @ViewChild('videoList', { static: false })
  protected videoListElement: ElementRef<HTMLUListElement>;

  @Input() titulo: string;
  @Input() hideList = false;
  @Input() hideShare = false;
  @Input() serie: SerieFormat;
  @Input() videos: Video[];

  protected renderListStatus = signal<'pending' | 'ready' | 'failure'>(
    'pending'
  );

  protected player: ShakaPlayerInstance | null = null;
  protected videoPlaying: Video | null = null;
  protected debugTimestamp = new Date().getSeconds();
  protected progressDownloadAll: Observable<ProgressDownload>[] = [];

  // Propriedades para controle de scroll
  protected isListScrolled = false;
  protected canScrollUp = false;
  protected canScrollDown = false;

  private destroy$ = new Subject<boolean>();
  private shakaTimeoutRef: NodeJS.Timeout | null = null;
  private offlineShaka: OfflineShaka | null = null;
  private isInitializing = false;

  constructor(
    @Inject(LOCATION_TOKEN) private location: Location,
    private toastService: ToastController,
    private modal: ModalService,
    private apiLiv: ApiLiv,
    private cdr: ChangeDetectorRef,
    private language: I18nService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();

    if (this.shakaTimeoutRef) {
      clearTimeout(this.shakaTimeoutRef);
      this.shakaTimeoutRef = null;
    }

    if (this.player) {
      try {
        this.player.removeEventListener('error', this.onError.bind(this));
        this.player.destroy();
      } catch (error) {
        console.error('Erro ao destruir player:', error);
      } finally {
        this.player = null;
      }
    }

    // Limpar instância offline
    this.offlineShaka = null;

    // Limpar referências dos vídeos
    if (this.videos) {
      this.videos = [];
    }
    this.videoPlaying = undefined;
    this.progressDownloadAll = [];
  }

  ngAfterViewInit(): void {
    shaka.polyfill.installAll();

    if (!shaka.Player.isBrowserSupported()) {
      this.toastService.warning({
        message: this.language.instant('messages.shaka.browser_not_supported')
      });
      return;
    }
    this.shakaTimeoutRef = setTimeout(() => void this.initPlayer(), 250);

    // Iniciar atualização periódica do estado dos downloads
    this.startDownloadStateUpdater();
    this.checkInitialScrollState();
  }

  private startDownloadStateUpdater(): void {
    // Atualizar estado dos downloads a cada 500ms
    interval(500)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateVideosDownloadState();
      });
  }

  private updateVideosDownloadState(): void {
    if (!this.videos || !this.offlineShaka) {
      return;
    }

    this.videos.forEach((video, index) => {
      const isInQueue = this.offlineShaka.isVideoInQueue(video.id);
      const isActivelyDownloading = this.offlineShaka.isVideoDownloading(
        video.id
      );

      // Log temporário para debug
      if (isInQueue || isActivelyDownloading) {
        // eslint-disable-next-line no-console
        console.log(
          `Video ${video.id}: InQueue=${isInQueue}, Downloading=${isActivelyDownloading}`
        );
      }

      // Atualizar apenas se o estado mudou
      if (
        video.isInQueue !== isInQueue ||
        video.isDownloading !== isActivelyDownloading
      ) {
        this.videos[index] = {
          ...video,
          isInQueue,
          isDownloading: isActivelyDownloading
        };
      }
    });
  }

  get isIOSOperatingSystem() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  private isPlayerReady(): boolean {
    return !!this.player && !this.player.isDestroyed;
  }

  private isStorageCorruptedError(error: any): boolean {
    return error?.code === (ShakaErrorCode.STORAGE_KEY_NOT_FOUND as number);
  }

  forceUpdateVideoPlaying(video: Video): void {
    const link = video.offlineUri ?? video.link;

    // Sempre atualizar o vídeo atual, mesmo que seja o mesmo ID
    // Isso é importante para casos de recuperação de erro
    this.videoPlaying = {
      ...video,
      link
    } as Video;

    if (this.isPlayerReady() && link) {
      this.player
        .load(link)
        .then(() => {
          // Vídeo carregado com sucesso
        })
        .catch((error: any) => {
          console.error('Erro ao carregar vídeo:', error);

          // Se falhou ao carregar e era um link offline, tentar com link online
          if (video.offlineUri && video.link) {
            console.warn(
              'Tentando carregar com link online após falha do offline'
            );
            this.videoPlaying = {
              ...video,
              link: video.link,
              offlineUri: undefined // Remove referência offline corrompida
            } as Video;

            // Tentar novamente com link online
            this.player
              .load(video.link)
              .then(() => {
                // Vídeo carregado com link online
              })
              .catch((onlineError: any) => {
                console.error('Erro ao carregar vídeo online:', onlineError);
                this.onError(onlineError);
              });
          } else {
            this.onError(error);
          }
        });
    }
  }

  getVideoUri(video: Video): string {
    return video.offlineUri ?? video.link;
  }

  onListScroll(event: Event): void {
    // No mobile, não aplicamos os gradientes pois o scroll é da página inteira
    const isMobile = window.innerWidth < 1024; // breakpoint lg do Tailwind

    if (isMobile) {
      this.canScrollUp = false;
      this.canScrollDown = false;
      this.isListScrolled = false;
      return;
    }

    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Detecta se pode scrollar para cima (já scrollou para baixo)
    this.canScrollUp = scrollTop > 10;

    // Detecta se pode scrollar para baixo (não chegou no final)
    this.canScrollDown = scrollHeight - scrollTop - clientHeight > 10;

    // Detecta se a lista está sendo scrollada
    this.isListScrolled = this.canScrollUp || this.canScrollDown;
  }

  private checkInitialScrollState(): void {
    if (this.videoListElement?.nativeElement) {
      const element = this.videoListElement.nativeElement;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      // Inicialmente, pode scrollar para baixo se há overflow
      this.canScrollDown = scrollHeight > clientHeight;
      this.canScrollUp = false;
      this.isListScrolled = false;
    }
  }

  async initPlayer(): Promise<void> {
    if (this.isInitializing) {
      console.warn('Player já está sendo inicializado');
      return;
    }

    this.isInitializing = true;

    try {
      if (
        !this.videoElement?.nativeElement ||
        !this.videoContainerElement?.nativeElement
      ) {
        console.error(
          this.language.instant('messages.shaka.videos_elements_error')
        );
        return;
      }

      await this.createShakaPlayer();
      this.initializeOfflineShaka();
      await this.loadOfflineVideos();
      this.loadFirstVideo();

      this.renderListStatus.set('ready');
    } catch (error) {
      console.error('Erro ao inicializar o player:', error);
      this.toastService.danger({
        message: this.language.instant(
          'messages.shaka.player_initialization_error'
        )
      });

      this.renderListStatus.set('failure');
    } finally {
      this.isInitializing = false;
    }
  }

  private async createShakaPlayer(): Promise<void> {
    this.player = new shaka.Player();
    await this.player.attach(this.videoElement.nativeElement);

    new shaka.ui.Overlay(
      this.player,
      this.videoContainerElement.nativeElement,
      this.videoElement.nativeElement
    );

    this.player.addEventListener('error', this.onError.bind(this));
  }

  private initializeOfflineShaka(): void {
    if (!this.offlineShaka || !this.player) {
      this.offlineShaka = new OfflineShaka(this.player, {
        maxConcurrentDownloads: MAX_CONCURRENT_DOWNLOADS_LIMIT
      });
    }
  }

  private getOfflineShaka(): OfflineShaka {
    if (!this.offlineShaka) {
      this.initializeOfflineShaka();
    }
    return this.offlineShaka!;
  }

  private async loadOfflineVideos(): Promise<void> {
    try {
      if (!this.videos || this.videos.length === 0) {
        console.warn('Nenhum vídeo disponível para verificar offline');
        return;
      }

      const offlineShaka = this.getOfflineShaka();
      const offlineVideos = await offlineShaka.getOfflineVideos(
        this.videos.map((video) => video.id),
        EOfflineStorageVideoType.serie
      );

      if (offlineVideos.length > 0) {
        this.videos = this.videos.reduce((acc, curr) => {
          const offlineVideo = offlineVideos.find(
            (offlineVideo) => offlineVideo.appMetadata?.id === curr.id
          );
          if (offlineVideo && offlineVideo.offlineUri) {
            if (
              acc.findIndex((a) => a.id === offlineVideo.appMetadata?.id) < 0
            ) {
              acc = [...acc, { ...curr, offlineUri: offlineVideo.offlineUri }];
            } else {
              acc = [...acc, curr];
            }
          } else {
            acc = [...acc, curr];
          }
          return acc;
        }, [] as Video[]);
      }
    } catch (error) {
      console.error('Erro ao inicializar vídeos offline:', error);

      // Se for erro de storage corrompido, tentar limpar
      if (this.isStorageCorruptedError(error)) {
        await this.handleCorruptedStorage();
      }
    }
  }

  private loadFirstVideo(): void {
    if (this.videos.length > 0) {
      const [video] = this.videos;
      this.forceUpdateVideoPlaying(video);
    }
  }

  private onError(event: any): void {
    // Extrair o erro do evento
    const error: ShakaError = event?.detail || event;

    console.error('Shaka Player Error:', {
      code: error?.code,
      category: error?.category,
      message: error?.message,
      severity: error?.severity,
      event
    });

    // Tratamento específico para erro de storage corrompido
    if (this.isStorageCorruptedError(error)) {
      console.warn('Storage corrompido detectado, tentando limpar...');
      this.handleCorruptedStorage();
      return;
    }

    if (error?.severity === ShakaErrorSeverity.RECOVERABLE) {
      // Recoverable error - logs técnicos
      console.warn(
        this.language.instant('messages.shaka.recuperable_error'),
        error?.message,
        error
      );
      this.toastService.warning({
        message: this.language.instant('messages.shaka.temporary_video_problem')
      });
    } else if (error?.severity === ShakaErrorSeverity.CRITICAL) {
      // Critical error - logs técnicos
      console.error(
        this.language.instant('messages.shaka.critical_error'),
        error?.message,
        error
      );
      this.toastService.danger({
        message: this.language.instant('messages.shaka.video_error')
      });
    } else {
      // Erro genérico ou indefinido - logs técnicos
      console.error(
        this.language.instant('messages.shaka.unknown_error'),
        error?.message,
        error
      );
      this.toastService.danger({
        message: this.language.instant('messages.shaka.video_loading_error')
      });
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private async handleCorruptedStorage(): Promise<void> {
    try {
      const offlineShaka = this.getOfflineShaka();
      await offlineShaka.clearAllOfflineStorage();

      this.toastService.info({
        message: this.language.instant('messages.shaka.clean_storage')
      });

      await this.cleanupAfterStorageCorruption();
      await this.reloadVideoAfterStorageCleanup();

      this.cdr.detectChanges();

      // Recarregar o vídeo atual se necessário
      if (this.videos?.length > 0) {
        // Se havia um vídeo tocando, tentar recarregá-lo
        if (this.videoPlaying) {
          const currentVideo = this.videos.find(
            (v) => v.id === this.videoPlaying?.id
          );
          if (currentVideo) {
            // Forçar uso do link online após limpeza do storage
            const onlineVideo = { ...currentVideo, offlineUri: undefined };
            this.forceUpdateVideoPlaying(onlineVideo);
          } else {
            // Se o vídeo atual não existe mais, carrega o primeiro
            const onlineVideo = { ...this.videos[0], offlineUri: undefined };
            this.forceUpdateVideoPlaying(onlineVideo);
          }
        } else {
          // Se não havia vídeo tocando, carrega o primeiro
          const onlineVideo = { ...this.videos[0], offlineUri: undefined };
          this.forceUpdateVideoPlaying(onlineVideo);
        }
      }

      this.cdr.detectChanges();

      this.toastService.success({
        message: this.language.instant('messages.shaka.success_reload')
      });
    } catch (error) {
      console.error(
        this.language.instant('messages.shaka.corrupted_storage'),
        error
      );
      this.toastService.danger({
        message: this.language.instant('messages.shaka.saving_error')
      });

      try {
        this.toastService.info({
          message: this.language.instant('messages.shaka.player_reloading')
        });
        await this.reinitializePlayer();
      } catch (reinitError) {
        console.error(
          this.language.instant('messages.shaka.reloading_error'),
          reinitError
        );

        this.toastService
          .danger({
            message: this.language.instant('messages.shaka.page_reload')
          })
          .dismiss()
          .then(() => window.location.reload());
      }
    }
  }

  private async reinitializePlayer(): Promise<void> {
    try {
      // Destruir player atual se existir
      if (this.player) {
        try {
          this.player.removeEventListener('error', this.onError.bind(this));
          await this.player.destroy();
        } catch (destroyError) {
          console.warn(
            this.language.instant('messages.shaka.player_destruction_error'),
            destroyError
          );
        }
        this.player = null;
      }

      // Limpar instância offline
      this.offlineShaka = null;

      // Reinicializar tudo
      await this.createShakaPlayer();
      this.initializeOfflineShaka();
      await this.loadOfflineVideos();
      this.loadFirstVideo();

      this.toastService.success({
        message: this.language.instant('messages.shaka.player_success_reload')
      });
    } catch (error) {
      console.error(
        this.language.instant('messages.shaka.reinicialization_error'),
        error
      );
      throw error; // Re-throw para ser capturado pelo método pai
    }
  }

  async allDownloads(): Promise<void> {
    if (!this.videos || this.videos.length === 0) {
      this.toastService.warning({
        message: this.language.instant('messages.shaka.no_video_avaliable')
      });
      return;
    }

    const offlineShaka = this.getOfflineShaka();
    const videosToDownload = this.videos.filter(
      (video) => !video.offlineUri && !offlineShaka.isVideoInQueue(video.id)
    );

    if (videosToDownload.length === 0) {
      this.toastService.info({
        message: this.language.instant('messages.shaka.queued_videos')
      });
      return;
    }

    this.toastService.info({
      message: this.language.instant('messages.shaka.add_videos', {
        videos: videosToDownload.length
      })
    });

    // Adicionar todos os vídeos à fila sem pausa - o OfflineShaka vai gerenciar
    const downloadPromises = videosToDownload.map(async (video) => {
      try {
        await this.onDownload(video);
      } catch (error) {
        console.error(
          this.language.instant('messages.shaka.video_download_error', {
            name: video.titulo
          }),
          error
        );
        // Continua com o próximo vídeo mesmo se um falhar
      }
    });

    // Não aguarda todos terminarem - deixa o OfflineShaka gerenciar a fila
    await Promise.allSettled(downloadPromises);
  }

  async onDownload(video: Video): Promise<void> {
    if (!this.canStartDownload(video)) {
      return;
    }

    try {
      this.toastService.info({
        message: this.language.instant('messages.shaka.starting_download_soon')
      });

      await this.performDownload(video);
      this.toastService.success({
        message: this.language.instant(
          'messages.shaka.video_download_successfull',
          { name: video.titulo }
        )
      });
    } catch (error) {
      await this.handleDownloadError(video, error);
    } finally {
      this.cleanupDownload(video);
    }
  }

  async onCancelDownload(video: Video): Promise<void> {
    try {
      const videoIndex = this.videos.findIndex((v) => v.id === video.id);
      if (videoIndex === -1) {
        return;
      }

      // Cancelar o download específico
      await this.getOfflineShaka().cancelSingleDownload(video.id);

      // Atualizar estado local
      this.videos[videoIndex] = {
        ...this.videos[videoIndex],
        isDownloading: false,
        isInQueue: false,
        download$: undefined
      };

      this.toastService.info({
        message: this.language.instant(
          'messages.shaka.video_download_canceled',
          { name: video.titulo }
        )
      });
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Erro ao cancelar download:', error);
      this.toastService.danger({
        message: this.language.instant('messages.shaka.cancel_download_error')
      });
    }
  }

  private canStartDownload(video: Video): boolean {
    if (video.offlineUri) {
      this.toastService.info({
        message: this.language.instant('messages.shaka.already_downloaded')
      });
      return false;
    }

    if (video.isDownloading || video.isInQueue) {
      this.toastService.warning({
        message: this.language.instant('messages.shaka.on_queue')
      });
      return false;
    }

    // Verificar se o vídeo já está na fila do OfflineShaka
    const offlineShaka = this.getOfflineShaka();
    if (offlineShaka.isVideoInQueue(video.id)) {
      this.toastService.warning({
        message: this.language.instant('messages.shaka.video_on_queue')
      });
      return false;
    }

    // Verificar limite total de downloads (ativos + fila)
    const totalInQueue = offlineShaka.getQueueLength();
    const maxQueueSize = MAX_CONCURRENT_DOWNLOADS_LIMIT * 3; // Até 3x o limite na fila (9 total)

    if (totalInQueue >= maxQueueSize) {
      this.toastService.warning({
        message: this.language.instant('messages.shaka.a_lot_of_downloads', {
          total: totalInQueue,
          max: maxQueueSize
        })
      });

      return false;
    }

    return true;
  }

  private async performDownload(video: Video): Promise<void> {
    const offlineShaka = this.getOfflineShaka();
    const videoIndex = this.videos.findIndex((v) => v.id === video.id);

    if (videoIndex === -1) {
      return;
    }

    this.setVideoDownloadingState(videoIndex, true, offlineShaka, video.id);

    try {
      const updatedVideo = this.videos[videoIndex];
      const metadata = await this.buildMetadata(updatedVideo);

      const downloadResult = await offlineShaka.download({
        link: this.getVideoUri(updatedVideo),
        metadata
      });

      this.setVideoCompletedState(videoIndex, downloadResult.offlineUri);
    } catch (error) {
      this.setVideoErrorState(videoIndex, video, error);
    }
  }

  private setVideoDownloadingState(
    videoIndex: number,
    isDownloading: boolean,
    offlineShaka: OfflineShaka,
    videoId: string
  ): void {
    const isInQueue = offlineShaka.isVideoInQueue(videoId);
    const isActivelyDownloading = offlineShaka.isVideoDownloading(videoId);

    this.videos[videoIndex] = {
      ...this.videos[videoIndex],
      isDownloading: isActivelyDownloading,
      isInQueue,
      download$: isDownloading
        ? offlineShaka
            .getProgressObservable(videoId)
            .pipe(takeUntil(this.destroy$))
        : undefined
    };
    this.cdr.detectChanges();
  }

  private setVideoCompletedState(videoIndex: number, offlineUri: string): void {
    this.videos[videoIndex] = {
      ...this.videos[videoIndex],
      offlineUri,
      isDownloading: false,
      isInQueue: false
    };
    this.cdr.detectChanges();
  }

  private setVideoErrorState(
    videoIndex: number,
    video: Video,
    error: unknown
  ): void {
    this.videos[videoIndex] = {
      ...this.videos[videoIndex],
      isDownloading: false,
      isInQueue: false,
      download$: undefined
    };
    this.cdr.detectChanges();

    const errorMessage =
      error instanceof Error
        ? error.message
        : this.language.instant('messages.shaka.unknown_error');
    this.toastService.danger({
      message: this.language.instant('messages.shaka.download_error', {
        name: video.titulo,
        error: errorMessage
      })
    });
  }

  private cleanupDownload(video: Video): void {
    video.isDownloading = false;
    video.isInQueue = false;
    video.download$ = undefined;
  }

  private async handleDownloadError(video: Video, error: any): Promise<void> {
    console.error(
      this.language.instant('messages.shaka.generic_download_error'),
      error
    );

    let errorMessage = this.language.instant(
      'messages.shaka.video_download_error',
      { name: video.titulo }
    );
    if (this.isStorageCorruptedError(error)) {
      errorMessage += this.language.instant('messages.shaka.save_problem');
      await this.handleCorruptedStorage();
    } else {
      errorMessage += `: ${error?.message || this.language.instant('messages.shaka.unknown_error')}`;
    }

    this.toastService.danger({ message: errorMessage });
  }

  // Métodos utilitários para estatísticas
  get totalVideos(): number {
    return this.videos?.length || 0;
  }

  get downloadedVideos(): number {
    return this.videos?.filter((v) => v.offlineUri).length || 0;
  }

  get downloadingVideos(): number {
    return this.videos?.filter((v) => v.isDownloading).length || 0;
  }

  get queuedVideos(): number {
    return this.getOfflineShaka()?.getQueueLength() || 0;
  }

  get downloadProgress(): number {
    if (this.totalVideos === 0) {
      return 0;
    }
    return Math.round((this.downloadedVideos / this.totalVideos) * 100);
  }

  get downloadStatus(): string {
    const downloading = this.downloadingVideos;
    const queued = this.queuedVideos;
    const active = this.getOfflineShaka()?.getActiveDownloadsCount() || 0;

    if (downloading > 0) {
      return this.language.instant('messages.shaka.active_and_queued', {
        active,
        queued
      }) as string;
    } else if (queued > 0) {
      return this.language.instant('messages.shaka.queued', {
        queued
      }) as string;
    }
    return this.language.instant('messages.shaka.no_active_download') as string;
  }

  // Método para cancelar todos os downloads
  async cancelAllDownloads(): Promise<void> {
    try {
      // Cancelar downloads no OfflineShaka (cancela downloads em progresso e limpa a fila)
      await this.getOfflineShaka().cancelDownload();

      // Limpar estado local dos vídeos
      if (this.videos) {
        this.videos = this.videos.map((video) => ({
          ...video,
          isDownloading: false,
          isInQueue: false,
          download$: undefined
        }));
      }

      this.toastService.info({
        message: this.language.instant('messages.shaka.downloads cancels')
      });
    } catch (error) {
      console.error(
        this.language.instant('messages.shaka.cancels_downloads_errors'),
        error
      );
      this.toastService.danger({
        message: this.language.instant(
          'messages.shaka.cancels_downloads_errors'
        )
      });
    }
  }

  private async buildMetadata(video: Video) {
    return {
      id: video.id,
      title: video.titulo,
      thumbnail: video.capa
        ? await BasicUtils.getFileUrl(
            (await BasicUtils.urlExists(
              BasicUtils.getThumbnail(video.capa).url
            ))
              ? BasicUtils.getThumbnail(video.capa).url
              : null
          )
        : null,
      serie: !!this.serie && {
        id: this.serie.id,
        title: this.serie.titulo,
        thumbnail: this.serie.thumbnail
          ? await BasicUtils.getFileUrl(
              (await BasicUtils.urlExists(this.serie.thumbnail))
                ? this.serie.thumbnail
                : null
            )
          : null
      },
      type: this.serie
        ? EOfflineStorageVideoType.serie
        : EOfflineStorageVideoType.video
    };
  }

  onShareLink(video: Video): void {
    if (!video.share?.loading) {
      const data = {
        link: video.link
      };
      Object.assign(video, { share: { loading: true } });
      this.apiLiv
        .post<string>('/Material/VideoLinks', data)
        .pipe(
          takeUntil(this.destroy$),
          catchError(() => {
            this.toastService.danger({
              message: this.language.instant('messages.shaka.copy_link_error')
            });
            return EMPTY;
          }),
          finalize(() => (video.share.loading = false))
        )
        .subscribe((data) => {
          video.share.link = 'assistir-video/' + data;

          this.modal
            .open<CopyVideoLinkModalData>(CopyLinkModalComponent, {
              data: {
                linkToCopy: `${this.location.origin}/${video.share.link}`
              }
            })
            .closed.pipe(take(1))
            .subscribe(() => {
              const video = this.videos.find((video) => video.share);
              delete video.share;
            });
        });
    }
  }

  private async cleanupAfterStorageCorruption(): Promise<void> {
    // Limpar estado local de forma mais segura
    if (this.videos) {
      this.videos = this.videos.map((video) => ({
        ...video,
        offlineUri: undefined,
        isDownloading: false,
        isInQueue: false,
        download$: undefined
      }));
    }

    // Recarregar os vídeos offline para atualizar o estado
    await this.loadOfflineVideos();
  }

  private async reloadVideoAfterStorageCleanup(): Promise<void> {
    // Recarregar o vídeo atual se necessário
    if (this.videos?.length > 0) {
      // Se havia um vídeo tocando, tentar recarregá-lo
      if (this.videoPlaying) {
        const currentVideo = this.videos.find(
          (v) => v.id === this.videoPlaying?.id
        );
        if (currentVideo) {
          // Forçar uso do link online após limpeza do storage
          const onlineVideo = { ...currentVideo, offlineUri: undefined };
          this.forceUpdateVideoPlaying(onlineVideo);
        } else {
          // Se o vídeo atual não existe mais, carrega o primeiro
          const onlineVideo = { ...this.videos[0], offlineUri: undefined };
          this.forceUpdateVideoPlaying(onlineVideo);
        }
      } else {
        // Se não havia vídeo tocando, carrega o primeiro
        const onlineVideo = { ...this.videos[0], offlineUri: undefined };
        this.forceUpdateVideoPlaying(onlineVideo);
      }
    }
  }

  private async handleCriticalStorageError(): Promise<void> {
    // Em caso de erro crítico, tentar reinicializar completamente
    try {
      this.toastService.info({
        message: this.language.instant('messages.shaka.player_reloading')
      });
      await this.reinitializePlayer();
    } catch (reinitError) {
      console.error(
        this.language.instant('messages.shaka.reloading_error'),
        reinitError
      );
      this.toastService.danger({
        message: this.language.instant('messages.shaka.critical_error_reload')
      });
    }
  }
}
